// components/OrderInfoBlock.js - The information block at the top of the order screen:
// customer, model, production and delivery date, commission number and name. The
// commission fields are typed in for now (source "manual") and filled by the Blueware /
// Vantage import later (source "import"); the server never lets an import replace
// something typed in, so a value shown as imported may still be overwritten by hand.
import React, { useState } from 'react';
import { SectionHeader } from './shared';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import api, { getErrorMessage } from '../utils/api';
import { formatDay } from '../utils/complaints';

function Field({ label, value, note }) {
  return (
    <div>
      <div className="meta-label">{label}</div>
      <div className="meta-value">
        {value || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>–</span>}
        {value && note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.75rem' }}> · {note}</span>}
      </div>
    </div>
  );
}

export default function OrderInfoBlock({ orderId, info, canEdit, onRegister, onInfoSaved }) {
  const { t } = useLang();
  const toast = useToast();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/orders/${orderId}/commission`, draft);
      onInfoSaved(data);
      setDraft(null);
      toast.success(t('oc.commission.saved'));
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const imported = (source) => (source === 'import' ? t('oc.source.import') : null);
  const actions = (
    <div style={{ display: 'flex', gap: 8 }}>
      {canEdit && !draft && (
        <button className="btn btn-ghost btn-sm"
          onClick={() => setDraft({ commission_no: info.commission_no || '', commission_name: info.commission_name || '' })}>
          ✎ {t('oc.commission.edit')}
        </button>
      )}
      {onRegister && <button className="btn btn-primary btn-sm" onClick={onRegister}>+ {t('oc.register')}</button>}
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <SectionHeader title={t('oc.info')} action={actions} />
      <div className="meta-grid">
        <Field label={t('complaints.customer')} value={[info.customer_code, info.customer_name].filter(Boolean).join(' · ')} />
        <Field label={t('complaints.model')} value={info.model} />
        <Field label={t('complaints.productiondate')} value={info.production_date && formatDay(info.production_date)} />
        <Field label={t('complaints.deliverydate')} value={info.delivery_date && formatDay(info.delivery_date)} />
        {!draft && (
          <>
            <Field label={t('complaints.commission')} value={info.commission_no} note={imported(info.commission_no_source)} />
            <Field label={t('oc.commissionname')} value={info.commission_name} note={imported(info.commission_name_source)} />
          </>
        )}
      </div>
      {draft && (
        <div style={{ marginTop: 14 }}>
          <div className="meta-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="commission-no">{t('complaints.commission')}</label>
              <input id="commission-no" className="input-field" value={draft.commission_no} maxLength={100}
                onChange={(e) => setDraft((d) => ({ ...d, commission_no: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="commission-name">{t('oc.commissionname')}</label>
              <input id="commission-name" className="input-field" value={draft.commission_name} maxLength={100}
                onChange={(e) => setDraft((d) => ({ ...d, commission_name: e.target.value }))} />
            </div>
          </div>
          <div className="form-hint" style={{ marginTop: 6 }}>{t('oc.commission.hint')}</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setDraft(null)}>{t('common.cancel')}</button>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? t('complaints.saving') : t('common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
